/**
 * Buy Widget Funnel Tracking
 *
 * Bridges the buy-widget lifecycle into PostHog as a provider-neutral funnel.
 * Runs on /buy wherever a buy widget is present. No experiments — the provider
 * is decided deterministically SERVER-SIDE by App\Experiment\WidgetRouter (geo +
 * MiCA capability map); this file only measures the funnel.
 *
 * Design:
 *  - The widgets push their lifecycle to window.dataLayer (Transak in
 *    src/js/components/transak.js; Simplex via the shared embed). We listen to
 *    dataLayer and forward a provider-neutral funnel to PostHog, tagged with the
 *    provider + country read from the widget's data-* attrs. We do NOT change the
 *    existing dataLayer/GTM behaviour.
 *  - Props are attached PER EVENT (not via posthog.register() super-properties),
 *    so an event never carries stale state from a previous page.
 *
 * FUNNEL COVERAGE IS ASYMMETRIC BY VENDOR DESIGN. Both widgets emit the SHARED
 * steps (exposure, load time, load failed, opened, checkout finished). Only
 * Transak emits the mid-funnel (KYC completed, order submitted, purchase error)
 * — Simplex's iframe never broadcasts them. Compare widgets ONLY on the shared
 * steps; the Transak-only steps are for diagnosing Transak's own drop-off.
 *
 * IMPORTANT — `buy_checkout_finished` is a SOFT signal. Both providers fire
 * their browser "finished/success" event when the user *finishes the checkout
 * UI*, NOT when payment is captured. The authoritative paid-conversion number
 * must come from the provider dashboards, not from this event. Treat this funnel
 * as engagement, not revenue.
 *
 * @see classes/Experiment/WidgetRouter.php                (geo provider decision)
 * @see src/js/components/transak.js                        (Transak → dataLayer)
 * @see template-parts/blocks/buy-widget-embeds/simplex-embed.php  (Simplex → dataLayer)
 * @see src/js/tracking/cta.js                              (posthog.capture pattern)
 */

/**
 * Any buy-widget root. The tracking runs wherever one is present, tagging events
 * with the provider/country from the widget's data-* attrs.
 */
const BUY_WIDGET_SELECTOR = [
  '[data-transak-widget]',
  '[data-transak-onramp-widget]',
  '[data-ab-provider]',
].join(',');

/**
 * Map raw dataLayer event names (pushed by the widgets) to the provider-neutral
 * funnel step we report to PostHog. Anything not listed is ignored.
 *
 * Coverage is asymmetric by vendor design (see header): Transak exposes KYC +
 * order-submitted; Simplex does not, so its mid-funnel is absent here and comes
 * from the provider report instead. Compare widgets only at shared steps
 * (buy_widget_exposed → buy_checkout_finished); the Transak-only steps are for
 * diagnosing WHERE Transak users drop off.
 */
const FUNNEL = {
  // --- SHARED steps (both widgets emit — the apples-to-apples comparison) ----
  // Transak
  transak_widget_loaded:     'buy_widget_load_time',   // carries load_ms
  transak_widget_load_failed:'buy_widget_load_failed',
  transak_widget_open:       'buy_widget_opened',
  transak_order_successful:  'buy_checkout_finished',  // SOFT — see header note
  // Simplex (hero-buy-ethereum.php, SDK/checkout mode)
  simplex_load_time:         'buy_widget_load_time',   // carries load_ms
  simplex_load_failed:       'buy_widget_load_failed',
  simplex_checkout_loaded:   'buy_widget_opened',
  simplex_flow_finished:     'buy_checkout_finished',  // SOFT — see header note

  // --- TRANSAK-ONLY steps (Simplex's iframe cannot emit these; for diagnosing
  //     WHERE Transak users drop off — NOT for cross-widget comparison) -------
  transak_kyc_completed:     'buy_kyc_completed',      // ORDER_CREATED = KYC done (real)
  transak_order_created:     'buy_order_submitted',
  transak_order_failed:      'buy_purchase_error',     // error DURING purchase
  transak_order_cancelled:   'buy_order_cancelled',

  // --- STUCK (Phase 0 instrumentation) — widget never reached its "ready" event
  //     within the watchdog window (Transak VPN/geo-block "wrap", or a silent
  //     iframe failure). Shared step across both providers. -------------------
  transak_widget_stuck:         'buy_widget_stuck',
  transak_onramp_widget_stuck:  'buy_widget_stuck',
  simplex_widget_stuck:         'buy_widget_stuck',

  // --- LAZY-LOAD FAILURE (Phase 1.2) — the on-demand Simplex loader could not
  //     bring the SDK up post page-load. Carries `stage` (script_load|init|
  //     create_form) for diagnosis. Distinct from _load_failed (SDK's own
  //     checkoutLoadFailed) — this is OUR loader failing before the SDK runs. --
  simplex_lazy_init_failed:     'buy_widget_lazy_init_failed',

  // --- TABS (Phase 1.2c) — user switched provider tab; engagement signal, carries
  //     from/to. Only fires in DUAL tab layouts. --------------------------------
  buy_widget_tab_switched:      'buy_widget_tab_switched',

  // --- FALLBACK (Phase 1.3) — active provider failed; a "try another provider"
  //     CTA was surfaced (_shown) / used (_clicked). from/to carry the swap. ---
  buy_widget_fallback_shown:    'buy_widget_fallback_shown',
  buy_widget_fallback_clicked:  'buy_widget_fallback_clicked',
};

let neutral = { provider: null, country: null }; // provider/country from the widget's data-* attrs
let exposureFired = false;

/** PostHog may load after us (it's deferred). Guard every call. */
function ph() {
  return typeof window.posthog !== 'undefined' &&
    typeof window.posthog.capture === 'function'
    ? window.posthog
    : null;
}

/**
 * The provider a raw dataLayer event belongs to, derived from its name prefix
 * (`transak_*` / `simplex_*`). Returns null for provider-neutral events (tab
 * switch, fallback CTA) so the caller can fall back to the page default.
 *
 * This must be per-EVENT, not per-page: on a DUAL/tabs page both providers emit
 * events under one page load, so a single page-wide provider would misattribute
 * every Simplex event to Transak (the primary) and vice-versa.
 */
function providerFromEvent(eventName) {
  if (typeof eventName !== 'string') return null;
  if (eventName.startsWith('transak_')) return 'transak';
  if (eventName.startsWith('simplex_')) return 'simplex';
  return null;
}

/**
 * Build the base properties attached to every buy-widget event: the page path,
 * the country (from the widget's data-* attrs, captured in init()), and the
 * provider. `buy_provider` in `extra` (derived per-event from the event name)
 * wins over the page default so DUAL/tabs pages attribute each event to the
 * provider that actually emitted it. Props are per-event only — no
 * posthog.register() super-properties, so nothing goes stale across pages.
 */
function baseProps(extra) {
  const base = {
    page_path:    window.location.pathname,
    buy_provider: neutral.provider,
    buy_country:  neutral.country,
  };

  return Object.assign(base, extra || {});
}

/**
 * Fire exposure once, when the assigned widget is genuinely on screen — not
 * merely present in the DOM. This keeps the denominator honest.
 */
function observeExposure() {
  const widget = document.querySelector(BUY_WIDGET_SELECTOR);
  if (!widget) return;

  const fire = () => {
    if (exposureFired) return;
    const p = ph();
    if (!p) return;
    exposureFired = true;
    p.capture('buy_widget_exposed', baseProps());
  };

  if (!('IntersectionObserver' in window)) {
    fire(); // Old browsers: count DOM presence as exposure.
    return;
  }

  const io = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        fire();
        io.disconnect();
      }
    });
  }, { threshold: 0.5 });

  io.observe(widget);
}

/** Forward a single dataLayer push to PostHog if it's a funnel event. */
function handleDataLayerPush(item) {
  if (!item || typeof item !== 'object') return;
  const step = FUNNEL[item.event];
  if (!step) return;

  const p = ph();
  if (!p) return;

  // Attribute the event to the provider it's about, overriding the page-wide
  // default in baseProps. On a DUAL/tabs page both providers fire under one page
  // load, so a page-wide provider would misattribute (e.g. Simplex-tab events
  // tagged 'transak', the primary). Two sources, in order:
  //   1. Prefixed lifecycle events (transak_* / simplex_*) → provider from name.
  //   2. Fallback events (buy_widget_fallback_shown/clicked) → item.from, which
  //      is the ACTIVE provider that failed (see buy-widget-tabs.js). Their name
  //      has no provider prefix, so without this they'd keep the page default.
  // Tab-switch and page-load exposure legitimately keep the page default.
  const extra = {};
  const eventProvider = providerFromEvent(item.event);
  if (eventProvider) {
    extra.buy_provider = eventProvider;
  } else if (item.event === 'buy_widget_fallback_shown' || item.event === 'buy_widget_fallback_clicked') {
    if (item.from) extra.buy_provider = item.from;
  }

  // Pass through the raw provider payload when present (Transak supplies order
  // data; Simplex supplies a result). Kept minimal — no PII forwarded.
  if (item.transak_order) {
    extra.order_amount_fiat = item.transak_order.fiatAmount;
    extra.order_fiat = item.transak_order.fiatCurrency;
    extra.order_crypto = item.transak_order.cryptoCurrency;
  }
  if (item.simplex_result) {
    extra.flow_result = item.simplex_result; // 'success' | 'failure'
  }
  if (typeof item.load_ms === 'number') {
    extra.load_ms = item.load_ms; // widget load time (both providers)
  }
  // Stuck-event diagnostics (Phase 0): how long we waited, the visitor country
  // (from data-buy-country), and the Transak postMessage type keys observed
  // (types only — never payloads).
  if (typeof item.elapsed_ms === 'number') {
    extra.elapsed_ms = item.elapsed_ms;
  }
  if (item.country) {
    extra.stuck_country = item.country;
  }
  if (Array.isArray(item.seen_message_types)) {
    extra.seen_message_types = item.seen_message_types;
  }
  if (item.stage) {
    extra.lazy_init_stage = item.stage; // duplicate_container | script_load | init | create_form
  }
  if (item.from || item.to) {
    extra.tab_from = item.from || null;
    extra.tab_to   = item.to   || null;
  }

  p.capture(step, baseProps(extra));
}

/**
 * Hook window.dataLayer so we see both events already queued and future pushes,
 * without disturbing GTM (which reads the same array).
 */
function tapDataLayer() {
  window.dataLayer = window.dataLayer || [];

  // Forward the widgets' lifecycle pushes to PostHog as funnel events.

  // Process anything already queued.
  window.dataLayer.forEach((item) => handleDataLayerPush(item));

  // Wrap push() so future events are processed too, without disturbing GTM.
  const originalPush = window.dataLayer.push.bind(window.dataLayer);
  window.dataLayer.push = function (...events) {
    events.forEach((item) => handleDataLayerPush(item));
    return originalPush(...events);
  };
}

function init() {
  // Run wherever a buy widget is present (any country).
  const widget = document.querySelector(BUY_WIDGET_SELECTOR);
  if (!widget) return;

  // Capture the provider + country from the widget's data-* attrs; baseProps
  // attaches them to every event. No posthog.register() — per-event only.
  neutral = {
    provider: widget.getAttribute('data-ab-provider') || null,
    country:  widget.getAttribute('data-buy-country') || null,
  };

  tapDataLayer();
  observeExposure();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
